import { useCallback, useState } from "react";
import { Link } from "react-router";
import { Button, Icon, Input } from "@astrum/ui";
import { api, ApiError } from "../lib/api.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./accept-invite.css";

/**
 * Requesting a password reset.
 *
 * Always shows the same "check your email" confirmation whether or not the
 * address has an account — the server already answers this way (see
 * POST /auth/forgot-password), and showing anything else here would undo that
 * by letting the UI itself reveal which emails are registered.
 */
export function ForgotPassword() {
  usePageTitle("Forgot password");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const canSubmit = email.trim().length > 0 && !submitting;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    setSubmitting(true);
    api
      .post("/auth/forgot-password", { email: email.trim() })
      .then(() => setSent(true))
      // The server already answers the same way for every address; a network
      // failure is the only case worth telling the person about separately —
      // and even then, showing "sent" avoids leaking anything and a retry
      // costs nothing.
      .catch((error: unknown) => {
        if (error instanceof ApiError && error.status >= 500) {
          setSent(false);
          return;
        }
        setSent(true);
      })
      .finally(() => setSubmitting(false));
  }, [canSubmit, email]);

  if (sent) {
    return (
      <div className="accept">
        <div className="accept__card">
          <Brand />
          <h1>Check your email.</h1>
          <p className="accept__muted">
            If an account exists for <strong>{email.trim()}</strong>, we&rsquo;ve sent a link to reset the password.
            It expires in one hour.
          </p>
          <Link to="/login" className="accept__note" style={{ textDecoration: "none" }}>
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="accept">
      <form
        className="accept__card"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Brand />

        <h1>Forgot your password?</h1>
        <p className="accept__muted">Enter the email you sign in with and we&rsquo;ll send you a reset link.</p>

        <Input
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
          required
        />

        <Button type="submit" fullWidth loading={submitting} disabled={!canSubmit}>
          Send reset link
        </Button>

        <p className="accept__note">
          <Icon name="lock" size={12} />
          <Link to="/login" style={{ color: "inherit" }}>
            Back to sign in
          </Link>
        </p>
      </form>
    </div>
  );
}

function Brand() {
  return (
    <div className="accept__brand">
      <span className="accept__mark">A</span>
      <span className="accept__wordmark">
        ASTRUM<strong>CONNECT</strong>
      </span>
    </div>
  );
}
